import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClinicStaff, addStaffToClinic, removeStaffFromClinic } from '../../store/thunks/clinicThunk';
import { logInfo, logError } from '../../utils/logger';
import Table from '../../applicationUi/components/Table';
import AddStaffToClinicModal from './AddStaffToClinicModal';

export default function ClinicStaff({ clinic, onUpdateClinic, editMode = false }) {
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [saveStatus, setSaveStatus] = useState('initial');
    const { staff, status, error } = useSelector((state) => state.clinics);

    useEffect(() => {
        if (clinic?._id) {
            dispatch(fetchClinicStaff(clinic._id))
                .unwrap()
                .then(() => {
                    logInfo('Clinic staff fetched successfully', { clinicId: clinic._id });
                })
                .catch((error) => {
                    logError('Failed to fetch clinic staff', { error, clinicId: clinic._id });
                });
        }
    }, [dispatch, clinic]);

    const handleAddStaff = async (staffData) => {
        setSaveStatus('saving');
        try {
            await dispatch(addStaffToClinic({ clinicId: clinic._id, staffData })).unwrap();
            logInfo('Staff member added successfully', { clinicId: clinic._id });
            setSaveStatus('saved');
            setIsModalOpen(false);
            onUpdateClinic();
            setTimeout(() => setSaveStatus('initial'), 2000);
        } catch (error) {
            logError('Failed to add staff member', { error, clinicId: clinic._id });
            setSaveStatus('error');
        }
    };

    const handleRemoveStaff = async (staffId) => {
        if (window.confirm('Are you sure you want to remove this staff member?')) {
            setSaveStatus('saving');
            try {
                await dispatch(removeStaffFromClinic({ clinicId: clinic._id, staffId })).unwrap();
                logInfo('Staff member removed successfully', { clinicId: clinic._id, staffId });
                setSaveStatus('saved');
                onUpdateClinic();
                setTimeout(() => setSaveStatus('initial'), 2000);
            } catch (error) {
                logError('Failed to remove staff member', { error, clinicId: clinic._id, staffId });
                setSaveStatus('error');
            }
        }
    };

    if (status === 'loading') {
        return (
            <div className="flex items-center justify-center h-32">
                <div className="text-sm text-gray-600">Loading staff data...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-32">
                <div className="text-sm text-red-600">Error: {error}</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                <div>
                    <h2 className="text-lg font-medium text-gray-900">Clinic Staff</h2>
                    <p className="text-sm text-gray-500 mt-1">Manage staff members assigned to this clinic</p>
                </div>
                {editMode && (
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-sm text-white bg-gray-900 hover:bg-gray-800 transition-colors"
                    >
                        Add Staff Member
                    </button>
                )}
            </div>

            {/* Staff List */}
            <div className="space-y-4">
                {staff && staff.length > 0 ? (
                    <div className="grid gap-4">
                        {staff.map((staffMember) => (
                            <div key={staffMember._id} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-sm">
                                <div className="flex items-center space-x-4">
                                    <div className="flex-shrink-0">
                                        <div className="h-10 w-10 rounded-sm bg-gray-300 flex items-center justify-center">
                                            <span className="text-sm font-medium text-gray-700">
                                                {staffMember.firstName?.[0]}{staffMember.lastName?.[0]}
                                            </span>
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900">
                                            {staffMember.firstName} {staffMember.lastName}
                                        </h3>
                                        <p className="text-sm text-gray-600">{staffMember.email}</p>
                                        <div className="flex items-center space-x-2 mt-1">
                                            {staffMember.role && Array.isArray(staffMember.role) ? (
                                                staffMember.role.map((role) => (
                                                    <span key={role} className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-gray-200 text-gray-800">
                                                        {role}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-gray-200 text-gray-800">
                                                    {staffMember.role || 'No role assigned'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {editMode && (
                                    <button
                                        onClick={() => handleRemoveStaff(staffMember._id)}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-sm text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        <div className="text-sm">No staff members assigned to this clinic.</div>
                        {editMode && (
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="mt-2 inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-sm text-white bg-gray-900 hover:bg-gray-800 transition-colors"
                            >
                                Add First Staff Member
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Status Messages */}
            {saveStatus === 'saved' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-sm">
                    <div className="text-sm text-green-800">Staff updated successfully!</div>
                </div>
            )}

            {saveStatus === 'error' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-sm">
                    <div className="text-sm text-red-800">Failed to update staff. Please try again.</div>
                </div>
            )}

            <AddStaffToClinicModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onAddStaff={handleAddStaff}
                clinicId={clinic._id}
                existingStaff={staff || []}
            />
        </div>
    );
}
